import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * What surrounds an article so it is not a dead end.
 *
 * A reader arriving from a search result lands on one post with no way back
 * to the list it belongs to, no sign that others exist, and nothing to read
 * next. Three small pieces fix that, and each is separately switchable
 * because they are opinions about someone else's site: a long-form essay may
 * want none of them.
 *
 * Plain elements and inline styles like the rest of the public renderer —
 * this runs on the brand's own domain, outside any theme of ours, and takes
 * the colours of the section it sits in.
 */

const linkStyle = (color) => ({
  color,
  textDecoration: 'none',
});

/** Home / Blog / This post. */
export function DetailBreadcrumb({ options, listPath, typeLabel, title, textColor }) {
  if (!options?.enabled) return null;

  const listLabel = options.listLabel || typeLabel || 'All';
  const crumbStyle = { opacity: 0.7, ...linkStyle(textColor) };

  return (
    <nav
      aria-label='Breadcrumb'
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
        fontSize: 13, marginBottom: 20, color: textColor,
      }}
    >
      <Link to='/' style={crumbStyle}>{options.homeLabel || 'Home'}</Link>
      {listPath && (
        <>
          <span style={{ opacity: 0.4 }}>/</span>
          <Link to={listPath} style={crumbStyle}>{listLabel}</Link>
        </>
      )}
      {title && (
        <>
          <span style={{ opacity: 0.4 }}>/</span>
          {/* The current page is not a link — a crumb that reloads the page
              you are on is a small lie about where it goes. */}
          <span style={{ opacity: 0.95, fontWeight: 600 }}>{title}</span>
        </>
      )}
    </nav>
  );
}

/** The one before and the one after, along the same list. */
export function DetailPrevNext({ options, siblings, currentSlug, listSlug, textColor }) {
  if (!options?.enabled || !listSlug) return null;

  const i = siblings.findIndex((s) => s.slug === currentSlug);
  if (i < 0) return null;

  // Newest first, so the one BEFORE in the list is the newer article. Naming
  // them by direction in the list rather than by date avoids arguing about
  // which of "previous" and "next" means older.
  const newer = siblings[i - 1];
  const older = siblings[i + 1];
  if (!newer && !older) return null;

  const box = {
    flex: '1 1 240px',
    minWidth: 0,
    padding: '14px 16px',
    borderRadius: 12,
    border: '1px solid rgba(127,127,127,0.22)',
    ...linkStyle(textColor),
  };

  return (
    <div
      style={{
        display: 'flex', flexWrap: 'wrap', gap: 12,
        margin: '36px auto 0', maxWidth: 760,
      }}
    >
      {newer && (
        <Link to={`/${listSlug}/${newer.slug}`} style={{ ...box, textAlign: 'left' }}>
          <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>← Previous</div>
          <div style={{ fontWeight: 600, lineHeight: 1.35 }}>{newer.title}</div>
        </Link>
      )}
      {older && (
        <Link to={`/${listSlug}/${older.slug}`} style={{ ...box, textAlign: 'right' }}>
          <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Next →</div>
          <div style={{ fontWeight: 600, lineHeight: 1.35 }}>{older.title}</div>
        </Link>
      )}
    </div>
  );
}

/** More from the same collection, optionally filterable. */
export function DetailMore({ options, siblings, currentSlug, listSlug, textColor }) {
  const [query, setQuery] = useState('');

  const others = useMemo(
    () => siblings.filter((s) => s.slug !== currentSlug),
    [siblings, currentSlug],
  );

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return others;
    return others.filter((s) => s.title.toLowerCase().includes(q));
  }, [others, query]);

  if (!options?.enabled || !listSlug || others.length === 0) return null;

  // Search shows everything it matches; without it the list is trimmed to the
  // chosen count. Searching inside a list of three would be silly, which is
  // why it is off unless asked for.
  const shown = options.showSearch ? matching : matching.slice(0, options.count || 3);

  return (
    <section style={{ margin: '48px auto 0', maxWidth: 1000, color: textColor }}>
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 12,
          alignItems: 'baseline', justifyContent: 'space-between',
          marginBottom: 18,
        }}
      >
        {options.title && (
          <h2 style={{ fontSize: 22, margin: 0, fontWeight: 700 }}>{options.title}</h2>
        )}
        {options.showSearch && (
          <input
            type='search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search…'
            aria-label='Search'
            style={{
              padding: '8px 12px',
              borderRadius: 999,
              border: '1px solid rgba(127,127,127,0.35)',
              background: 'rgba(127,127,127,0.06)',
              color: textColor,
              fontSize: 14,
              fontFamily: 'inherit',
              minWidth: 180,
            }}
          />
        )}
      </div>

      {shown.length === 0 ? (
        <p style={{ opacity: 0.7, fontSize: 14, margin: 0 }}>
          Nothing matches “{query}”.
        </p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(230px, 100%), 1fr))',
            gap: 20,
          }}
        >
          {shown.map((s) => (
            <Link key={s.slug} to={`/${listSlug}/${s.slug}`} style={linkStyle(textColor)}>
              {s.image && (
                <img
                  src={s.image}
                  alt=''
                  style={{
                    width: '100%', aspectRatio: '16 / 10', objectFit: 'cover',
                    borderRadius: 10, marginBottom: 8, display: 'block',
                  }}
                />
              )}
              <div style={{ fontWeight: 600, lineHeight: 1.35 }}>{s.title}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
